import React, { useState, useEffect } from 'react';
import {
  Card,
  Tabs,
  Form,
  Input,
  Select,
  Button,
  Table,
  Space,
  Tag,
  message,
  Row,
  Col,
  DatePicker,
  Tooltip,
  Typography
} from 'antd';
import {
  PlusCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  DollarOutlined,
  TagOutlined,
  UnorderedListOutlined,
  CoffeeOutlined
} from '@ant-design/icons';
import EditModal from '../../components/Modals/EditModal';
import Layout from '../../layout/Layout';
import fetchData from '../../functions/fetchData';
import updateData from '../../functions/updateData';
import ExportButtons from '../../components/ExportButtons';
import { fetchComboData } from '../../services/api';
import axios from 'axios';
import { getHeaders } from '../../utility/getHeader';
import dayjs from 'dayjs';
import logo from '../../assets/logo.png';

const { Option } = Select;

const BarManagement = () => {
  const [activeTab, setActiveTab] = useState('category');
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  // Category Management State
  const [editModal, setEditModal] = useState({ show: false, record: null, type: '' });
  const [categories, setCategories] = useState([]);
  const [categoryForm] = Form.useForm();

  // Item Management State
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [itemForm] = Form.useForm();

  // Pricing Management State
  const [prices, setPrices] = useState([]);
  const [pricingForm] = Form.useForm();

  // Search and Pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchCategories();
    fetchItems();
    fetchPrices();
    fetchSuppliers();
  }, []);

  // Fetch Categories
  const fetchCategories = async () => {
    try {
      await fetchData('brand', setCategories, 'id', { flag: 1 });
    } catch (error) {
      console.error('Error fetching categories:', error);
      messageApi.error('Failed to fetch categories');
    }
  };

  // Fetch Suppliers for Sponsor dropdown
  const fetchSuppliers = async () => {
    try {
      const supplierData = await fetchComboData('supplier', 'name');
      console.log('Suppliers data:', supplierData);
      setSuppliers(supplierData || []);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      messageApi.error('Failed to fetch suppliers');
    }
  };

  // Fetch Items
  const fetchItems = async () => {
    try {
      await fetchData('items', setItems, 'id', { flag: 1 });
    } catch (error) {
      console.error('Error fetching items:', error);
      messageApi.error('Failed to fetch items');
    }
  };

  // Fetch Prices
  const fetchPrices = async () => {
    try {
      await fetchData('bar_rate', setPrices, 'id', { flag: 1 });
    } catch (error) {
      console.error('Error fetching prices:', error);
      messageApi.error('Failed to fetch prices');
    }
  };

  // Handle Category Submit
  const handleCategorySubmit = async (values) => {
    try {
      setLoading(true);
      
      const response = await updateData('brand', values, {}, 'POST');
      if (response && response.success) {
        messageApi.success('Category added successfully!');
        categoryForm.resetFields();
        fetchCategories();
      } else {
        messageApi.error('Error adding category');
      }
    } catch (error) {
      console.error('Error adding category:', error);
      messageApi.error('Error adding category');
    } finally {
      setLoading(false);
    }
  };

  // Handle Edit Save (top-level, accessible to modal)
  const handleEditSave = async (updatedRecord) => {
    try {
      setEditModal({ show: false, record: null, type: '' });
      setLoading(true);
      
      let table = '';
      if (editModal.type === 'item') table = 'items';
      if (editModal.type === 'pricing') table = 'bar_rate';
      
      // Use the new updatecommondata API
      const response = await axios.put(
        `/updatecommondata/${table}/id/${updatedRecord.id}`,
        updatedRecord,
        getHeaders()
      );
      
      if (response.data && response.data.success) {
        messageApi.success('Record updated successfully!');
        if (editModal.type === 'item') fetchItems();
        if (editModal.type === 'pricing') fetchPrices();
      } else {
        messageApi.error('Error updating record');
      }
    } catch (error) {
      console.error('Error updating record:', error);
      messageApi.error('Error updating record');
    } finally {
      setLoading(false);
    }
  };

  const handleItemSubmit = async (values) => {
    try {
      setLoading(true);
      
      const response = await updateData('items', values, {}, 'POST');
      if (response && response.success) {
        messageApi.success('Item added successfully!');
        itemForm.resetFields();
        fetchItems();
      } else {
        messageApi.error('Error adding item');
      }
    } catch (error) {
      console.error('Error adding item:', error);
      messageApi.error('Error adding item');
    } finally {
      setLoading(false);
    }
  };

  // Handle Pricing Submit
  const handlePricingSubmit = async (values) => {
    try {
      setLoading(true);
      
      const response = await updateData('bar_rate', values, {}, 'POST');
      if (response && response.success) {
        messageApi.success('Pricing added successfully!');
        pricingForm.resetFields();
        fetchPrices();
      } else {
        messageApi.error('Error adding pricing');
      }
    } catch (error) {
      console.error('Error adding pricing:', error);
      messageApi.error('Error adding pricing');
    } finally {
      setLoading(false);
    }
  };

  // Handle Status Toggle (Activate/Deactivate)
  const handleStatusToggle = async (price) => {
    try {
      setLoading(true);
      
      // If category is empty/null, set to Regular (activate), otherwise set to empty (deactivate)
      const newCategory = (!price.category || price.category === '') ? 'Regular' : '';
      const statusText = newCategory === 'Regular' ? 'activated' : 'deactivated';
      
      const whereCondition = { id: price.id };
      const updateData_obj = { category: newCategory };
      
      const response = await updateData('bar_rate', updateData_obj, whereCondition, 'PUT');
      
      if (response && response.success) {
        messageApi.success(`Item ${statusText} successfully!`);
        fetchPrices();
      } else {
        messageApi.error('Error updating status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      messageApi.error('Error updating status');
    } finally {
      setLoading(false);
    }
  };

  // Search and Filter functionality
  const getFilteredData = (data, searchFields) => {
    if (!searchTerm) return data;
    
    return data.filter(item =>
      searchFields.some(field =>
        item[field]?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  };



  const renderCategoryTab = () => {
    const filteredCategories = getFilteredData(categories, ['name']);

    const columns = [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 80,
        sorter: (a, b) => a.id - b.id,
      },
      {
        title: 'Category Name',
        dataIndex: 'name',
        key: 'name',
        sorter: (a, b) => a.name.localeCompare(b.name),
        render: (text) => <strong>{text}</strong>,
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 150,
        render: (_, record) => (
          <Space>
            <Tooltip title="Edit">
              <Button type="primary" icon={<EditOutlined />} size="small" />
            </Tooltip>
            <Tooltip title="Delete">
              <Button type="primary" danger icon={<DeleteOutlined />} size="small" />
            </Tooltip>
          </Space>
        ),
      },
    ];

    return (
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={8}>
          <Card
            title={
              <Space>
                <PlusCircleOutlined />
                Add New Category
              </Space>
            }
            size="small"
          >
            <Form form={categoryForm} layout="vertical" onFinish={handleCategorySubmit}>
              <Form.Item
                label="Category Name"
                name="name"
                rules={[{ required: true, message: 'Please enter category name' }]}
              >
                <Input placeholder="Enter category name" />
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading} block>
                  Add Category
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={16}>
          <Card
            title={
              <Space>
                <UnorderedListOutlined />
                Existing Categories ({filteredCategories.length})
              </Space>
            }
            extra={
              <Space>
                <Input
                  placeholder="Search categories..."
                  prefix={<SearchOutlined />}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ width: 200 }}
                  allowClear
                />
                <ExportButtons
                  data={filteredCategories}
                  filename="bar-categories"
                  title="Bar Categories Report"
                  columns={[
                    { key: 'id', label: 'ID' },
                    { key: 'name', label: 'Category Name' }
                  ]}
                  size="sm"
                  logo={logo}
                />
              </Space>
            }
            size="small"
          >
            <Table
              columns={columns}
              dataSource={filteredCategories}
              rowKey="id"
              pagination={{
                pageSize: itemsPerPage,
                current: currentPage,
                onChange: (page) => setCurrentPage(page),
                showSizeChanger: false,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              }}
              size="small"
              loading={loading}
            />
          </Card>
        </Col>
      </Row>
    );
  };

  const renderItemTab = () => {
    const filteredItems = getFilteredData(items, ['brand', 'name', 'code']);

    const columns = [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 70,
        sorter: (a, b) => a.id - b.id,
      },
      {
        title: 'Brand',
        dataIndex: 'brand',
        key: 'brand',
        width: 120,
        render: (text) => <Tag color="blue">{text}</Tag>,
      },
      {
        title: 'Sponsor Name',
        dataIndex: 'sponsor_name',
        key: 'sponsor_name',
        width: 150,
        render: (text) => text ? <Tag color="cyan">{text}</Tag> : '—',
      },
      {
        title: 'Item Name',
        dataIndex: 'name',
        key: 'name',
        sorter: (a, b) => a.name.localeCompare(b.name),
        render: (text) => <strong>{text}</strong>,
      },
      {
        title: 'Code',
        dataIndex: 'code',
        key: 'code',
        width: 100,
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 150,
        render: (_, record) => (
          <Space>
            <Tooltip title="Edit">
              <Button
                type="primary"
                icon={<EditOutlined />}
                size="small"
                onClick={() => setEditModal({ show: true, record, type: 'item' })}
              />
            </Tooltip>
            <Tooltip title="Delete">
              <Button type="primary" danger icon={<DeleteOutlined />} size="small" />
            </Tooltip>
          </Space>
        ),
      },
    ];

    return (
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={8}>
          <Card
            title={
              <Space>
                <PlusCircleOutlined />
                Add New Item
              </Space>
            }
            size="small"
          >
            <Form form={itemForm} layout="vertical" onFinish={handleItemSubmit}>
              <Form.Item
                label="Brand/Category"
                name="brand"
                rules={[{ required: true, message: 'Please select brand' }]}
              >
                <Select placeholder="Select Brand">
                  {categories.map((category) => (
                    <Select.Option key={category.id} value={category.name}>
                      {category.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item label="Sponsor Name" name="sponsor_name">
                <Select placeholder="Select Sponsor" allowClear>
                  {suppliers.map((supplier) => (
                    <Select.Option key={supplier.id} value={supplier.name}>
                      {supplier.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label="Item Name"
                name="name"
                rules={[{ required: true, message: 'Please enter item name' }]}
              >
                <Input placeholder="Enter item name" />
              </Form.Item>

              <Form.Item
                label="Item Code"
                name="code"
                rules={[{ required: true, message: 'Please enter item code' }]}
              >
                <Input type="number" placeholder="Enter item code" />
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading} block>
                  Add Item
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={16}>
          <Card
            title={
              <Space>
                <UnorderedListOutlined />
                Existing Items ({filteredItems.length})
              </Space>
            }
            extra={
              <Space>
                <Input
                  placeholder="Search items..."
                  prefix={<SearchOutlined />}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ width: 200 }}
                  allowClear
                />
                <ExportButtons
                  data={filteredItems}
                  filename="bar-items"
                  title="Bar Items Report"
                  columns={[
                    { key: 'id', label: 'ID' },
                    { key: 'brand', label: 'Brand' },
                    { key: 'sponsor_name', label: 'Sponsor Name' },
                    { key: 'name', label: 'Item Name' },
                    { key: 'code', label: 'Code' }
                  ]}
                  size="sm"
                  logo={logo}
                />
              </Space>
            }
            size="small"
          >
            <Table
              columns={columns}
              dataSource={filteredItems}
              rowKey="id"
              pagination={{
                pageSize: itemsPerPage,
                current: currentPage,
                onChange: (page) => setCurrentPage(page),
                showSizeChanger: false,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              }}
              size="small"
              loading={loading}
            />
          </Card>
        </Col>
      </Row>
    );
  };

  const renderPricingTab = () => {
    const filteredPrices = getFilteredData(prices, ['brand', 'name', 'full', 'category']);

    const columns = [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 70,
        sorter: (a, b) => a.id - b.id,
      },
      {
        title: 'Brand',
        dataIndex: 'brand',
        key: 'brand',
        width: 120,
        render: (text) => <Tag color="blue">{text}</Tag>,
      },
      {
        title: 'Item Name',
        dataIndex: 'name',
        key: 'name',
        sorter: (a, b) => a.name.localeCompare(b.name),
        render: (text) => <strong>{text}</strong>,
      },
      {
        title: 'Full Price',
        dataIndex: 'full',
        key: 'full',
        width: 120,
        sorter: (a, b) => parseFloat(a.full) - parseFloat(b.full),
        render: (text) => <span style={{ color: '#52c41a', fontWeight: 'bold' }}>{text}</span>,
      },
      {
        title: 'Category',
        dataIndex: 'category',
        key: 'category',
        width: 120,
        render: (text) =>
          text && text !== '' ? (
            <Tag color="cyan">{text}</Tag>
          ) : (
            <Tag color="default">Inactive</Tag>
          ),
      },
      {
        title: 'Zone',
        dataIndex: 'zone',
        key: 'zone',
        width: 120,
        render: (text) => <Tag color="purple">{text}</Tag>,
      },
      {
        title: 'Status',
        key: 'status',
        width: 120,
        render: (_, record) =>
          record.category && record.category !== '' ? (
            <Button
              size="small"
              type="default"
              icon={<PauseCircleOutlined />}
              onClick={() => handleStatusToggle(record)}
              loading={loading}
            >
              Deactivate
            </Button>
          ) : (
            <Button
              size="small"
              type="primary"
              icon={<PlayCircleOutlined />}
              onClick={() => handleStatusToggle(record)}
              loading={loading}
            >
              Activate
            </Button>
          ),
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 150,
        render: (_, record) => (
          <Space>
            <Tooltip title="Edit">
              <Button
                type="primary"
                icon={<EditOutlined />}
                size="small"
                onClick={() => setEditModal({ show: true, record, type: 'pricing' })}
              />
            </Tooltip>
            <Tooltip title="Delete">
              <Button type="primary" danger icon={<DeleteOutlined />} size="small" />
            </Tooltip>
          </Space>
        ),
      },
    ];

    return (
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={8}>
          <Card
            title={
              <Space>
                <PlusCircleOutlined />
                Add Item Pricing
              </Space>
            }
            size="small"
          >
            <Form form={pricingForm} layout="vertical" onFinish={handlePricingSubmit}>
              <Form.Item
                label="Date"
                name="date"
                rules={[{ required: true, message: 'Please select date' }]}
              >
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>

              <Form.Item
                label="Brand"
                name="brand"
                rules={[{ required: true, message: 'Please select brand' }]}
              >
                <Select placeholder="Select Brand">
                  {categories.map((category) => (
                    <Select.Option key={category.id} value={category.name}>
                      {category.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label="Item Name"
                name="name"
                rules={[{ required: true, message: 'Please select item' }]}
              >
                <Select placeholder="Select Item">
                  {items
                    .filter((item) => {
                      const brand = pricingForm.getFieldValue('brand');
                      return brand ? item.brand === brand : true;
                    })
                    .map((item) => (
                      <Select.Option key={item.id} value={item.name}>
                        {item.name}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>

              <Form.Item
                label="Price (฿)"
                name="price"
                rules={[{ required: true, message: 'Please enter price' }]}
              >
                <Input type="number" placeholder="Enter price" min="0" step="0.01" />
              </Form.Item>

              <Form.Item label="Category" name="category">
                <Select placeholder="Select Category">
                  <Select.Option value="club">Club</Select.Option>
                  <Select.Option value="pool">Pool</Select.Option>
                  <Select.Option value="skybar">Skybar</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item label="Zone" name="zone">
                <Select placeholder="Select Zone">
                  <Select.Option value="club">Club</Select.Option>
                  <Select.Option value="restaurant">Restaurant</Select.Option>
                  <Select.Option value="bar">Bar</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading} block>
                  Add Pricing
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={16}>
          <Card
            title={
              <Space>
                <UnorderedListOutlined />
                Item Pricing ({filteredPrices.length})
              </Space>
            }
            extra={
              <Space>
                <Input
                  placeholder="Search pricing..."
                  prefix={<SearchOutlined />}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ width: 200 }}
                  allowClear
                />
                <ExportButtons
                  data={filteredPrices}
                  filename="bar-pricing"
                  title="Bar Pricing Report"
                  columns={[
                    { key: 'id', label: 'ID' },
                    { key: 'brand', label: 'Brand' },
                    { key: 'name', label: 'Item Name' },
                    { key: 'full', label: 'Price (฿)', formatter: (value) => `฿${value}` },
                    { key: 'category', label: 'Price Category', formatter: (value) => value && value !== '' ? value : 'Inactive' },
                    { key: 'zone', label: 'Zone' }
                  ]}
                  size="sm"
                  logo={logo}
                />
              </Space>
            }
            size="small"
          >
            <Table
              columns={columns}
              dataSource={filteredPrices}
              rowKey="id"
              pagination={{
                pageSize: itemsPerPage,
                current: currentPage,
                onChange: (page) => setCurrentPage(page),
                showSizeChanger: false,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              }}
              size="small"
              loading={loading}
            />
          </Card>
        </Col>
      </Row>
    );
  };

  return (
    <Layout>
      {contextHolder}
      <Card
        title={
          <Space>
            <CoffeeOutlined />
            <span>Bar Management</span>
          </Space>
        }
        extra={<Typography.Text type="secondary">Manage bar categories, items, and pricing</Typography.Text>}
      >
        <Tabs
          activeKey={activeTab}
          onChange={(key) => {
            setActiveTab(key);
            setCurrentPage(1);
            setSearchTerm('');
          }}
          items={[
            {
              key: 'category',
              label: (
                <Space>
                  <TagOutlined />
                  Categories
                </Space>
              ),
              children: renderCategoryTab(),
            },
            {
              key: 'items',
              label: (
                <Space>
                  <CoffeeOutlined />
                  Items
                </Space>
              ),
              children: renderItemTab(),
            },
            {
              key: 'pricing',
              label: (
                <Space>
                  <DollarOutlined />
                  Pricing
                </Space>
              ),
              children: renderPricingTab(),
            },
          ]}
        />

        {editModal.show && (
          <EditModal
            record={editModal.record}
            onClose={() => setEditModal({ show: false, record: null, type: '' })}
            onSave={handleEditSave}
          />
        )}
      </Card>
    </Layout>
  );
};

export default BarManagement;
